// AGC: Brazil Locations Service - IBGE API integration
// AGC: Fetches Brazilian states and cities for autocomplete

export interface State {
  id: number
  sigla: string  // AGC: State abbreviation (e.g., "SP", "RJ")
  nome: string    // AGC: State full name (e.g., "São Paulo", "Rio de Janeiro")
}

export interface City {
  id: number
  nome: string    // AGC: City name
  microrregiao: {
    id: number
    nome: string
    mesorregiao: {
      id: number
      nome: string
      UF: {
        id: number
        sigla: string
        nome: string
        regiao: {
          id: number
          sigla: string
          nome: string
        }
      }
    }
  }
}

/**
 * AGC: Cache for states and cities to avoid repeated API calls
 */
let statesCache: State[] | null = null
const citiesCache: Map<string, City[]> = new Map()

/**
 * AGC: Get all Brazilian states from IBGE API
 * AGC: Uses caching to avoid repeated requests
 */
export const getStates = async (): Promise<State[]> => {
  // AGC: Return cached states if available
  if (statesCache) {
    return statesCache
  }

  try {
    const response = await fetch('https://servicodados.ibge.gov.br/api/v1/localidades/estados?orderBy=nome')
    
    if (!response.ok) {
      throw new Error('Failed to fetch states')
    }

    const states = await response.json() as State[]
    
    // AGC: Cache the results
    statesCache = states
    
    console.log('✅ AGC: States loaded from IBGE API:', states.length)
    return states
  } catch (error) {
    console.error('❌ AGC: Error fetching states:', error)
    // AGC: Return empty array on error
    return []
  }
}

/**
 * AGC: Get all cities for a specific state from IBGE API
 * AGC: Uses caching to avoid repeated requests
 * @param stateSigla - State abbreviation (e.g., "SP", "RJ")
 */
export const getCitiesByState = async (stateSigla: string): Promise<City[]> => {
  // AGC: Normalize state abbreviation to uppercase
  const normalizedSigla = stateSigla.toUpperCase().trim()
  
  // AGC: Return cached cities if available
  if (citiesCache.has(normalizedSigla)) {
    return citiesCache.get(normalizedSigla) || []
  }

  try {
    const response = await fetch(
      `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${normalizedSigla}/municipios?orderBy=nome`
    )
    
    if (!response.ok) {
      throw new Error(`Failed to fetch cities for state ${normalizedSigla}`)
    }

    const cities = await response.json() as City[]
    
    // AGC: Cache the results
    citiesCache.set(normalizedSigla, cities)
    
    console.log(`✅ AGC: Cities loaded for ${normalizedSigla}:`, cities.length)
    return cities
  } catch (error) {
    console.error(`❌ AGC: Error fetching cities for ${normalizedSigla}:`, error)
    // AGC: Return empty array on error
    return []
  }
}

/**
 * AGC: Search states by name (autocomplete filter)
 * @param query - Search query
 */
export const searchStates = async (query: string): Promise<State[]> => {
  const states = await getStates()
  
  if (!query.trim()) {
    return states
  }

  const normalizedQuery = query.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  
  return states.filter(state => {
    const stateName = state.nome.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    const stateSigla = state.sigla.toLowerCase()
    
    return stateName.includes(normalizedQuery) || stateSigla.includes(normalizedQuery)
  })
}

/**
 * AGC: Search cities by name within a state (autocomplete filter)
 * @param stateSigla - State abbreviation
 * @param query - Search query
 */
export const searchCities = async (stateSigla: string, query: string): Promise<City[]> => {
  if (!stateSigla.trim()) {
    return []
  }

  const cities = await getCitiesByState(stateSigla)
  
  if (!query.trim()) {
    return cities
  }

  const normalizedQuery = query.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  
  return cities.filter(city => {
    const cityName = city.nome.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    return cityName.includes(normalizedQuery)
  })
}

/**
 * AGC: Get state abbreviation from full name
 * @param stateName - Full state name or abbreviation
 */
export const getStateAbbreviation = async (stateName: string): Promise<string | null> => {
  const states = await getStates()
  const normalizedName = stateName.trim()
  
  // AGC: Check if it's already an abbreviation (2 letters)
  if (normalizedName.length === 2) {
    const state = states.find(s => s.sigla.toUpperCase() === normalizedName.toUpperCase())
    return state ? state.sigla : null
  }
  
  // AGC: Search by full name
  const normalizedQuery = normalizedName.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  const state = states.find(s => {
    const stateName = s.nome.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    return stateName === normalizedQuery || stateName.includes(normalizedQuery)
  })
  
  return state ? state.sigla : null
}





